import { cssVars } from '@lmc/design-system/tokens'
import { Card, PageHeader, Spinner } from '@lmc/ui-kit'
import useResident from 'hooks/useResident'
import { NAMESPACE } from 'locale'
import { emarV2Constants } from 'locale/emarV2'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import PageWrapper from '../componentsV2/PageWrapper'
import { useAddMedicationButtonState } from '../hooks/useAddMedicationButtonState'
import { useAddMedicationFacelift } from '../hooks/useAddMedicationFacelift'
import { useAddMedicationForm } from '../hooks/useAddMedicationForm'
import { AddMedicationConfirmModal } from './AddMedicationConfirmModal'
import { AddMedicationFooter } from './AddMedicationFooter'
import { AddMedicationStepper } from './AddMedicationStepper'
import { AddMedicationSummary } from './AddMedicationSummary'
import { ADD_MEDICATION_STEPS } from './addMedicationSteps'

const getMedicationListPath = (residentId: string | undefined) =>
  `/residents/${residentId}/emar/medication-list`

/** The screen the add medication button sent the user from, if it did. */
const getFromPath = (state: unknown) =>
  state &&
  typeof state === 'object' &&
  'from' in state &&
  typeof state.from === 'string'
    ? state.from
    : undefined

export const AddMedicationScreen = () => {
  const { id: residentId } = useParams<{ id: string }>()
  const { resident, isLoading: isLoadingResident } = useResident({
    id: residentId
  })
  const { isEnabled, isLoading: isLoadingFlags } = useAddMedicationFacelift()
  const { disabled: cannotAddMedication } =
    useAddMedicationButtonState(resident)

  if (isLoadingFlags || isLoadingResident || !resident) {
    return (
      <PageWrapper>
        <Spinner style={{ marginTop: '80px' }} />
      </PageWrapper>
    )
  }

  // Reached by URL with the flag off, or by someone the add medication button
  // would have stopped
  if (!isEnabled || cannotAddMedication) {
    return <Navigate to={getMedicationListPath(residentId)} replace />
  }

  return <AddMedicationPage residentId={resident.id} />
}

/** Past the gates, so the form starts once the resident has loaded. */
const AddMedicationPage = ({ residentId }: { residentId: string }) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)
  const navigate = useNavigate()
  const location = useLocation()
  const { fields, formState, isDirty } = useAddMedicationForm(residentId)
  const [currentStepIndex, setCurrentStepIndex] = useState(0)
  const [isConfirmingCancel, setIsConfirmingCancel] = useState(false)

  // Replace, so the browser's back button doesn't reopen an abandoned page.
  // Opened by URL there's no `from`, so fall back to the medication list.
  const leave = () =>
    navigate(getFromPath(location.state) ?? getMedicationListPath(residentId), {
      replace: true
    })

  return (
    <>
      <PageHeader title={t(emarV2Constants.ADD_MEDICATION_TITLE)} sticky />
      <PageWrapper>
        <StepsCard>
          <StepsHeader>
            <AddMedicationStepper currentStepIndex={currentStepIndex} />
            <AddMedicationSummary
              medicationName={formState.medicationName}
              isControlledDrug={Boolean(fields.controlledDrug)}
              isPrn={typeof fields.PRN === 'boolean' ? fields.PRN : undefined}
            />
          </StepsHeader>
          <AddMedicationFooter
            isFirstStep={currentStepIndex === 0}
            isLastStep={currentStepIndex === ADD_MEDICATION_STEPS.length - 1}
            // No step has fields yet, so none has mandatory ones to wait for
            canProceed
            onBack={() => setCurrentStepIndex((index) => index - 1)}
            // Nothing entered means nothing to lose, so no need to ask
            onCancel={() => (isDirty ? setIsConfirmingCancel(true) : leave())}
            onNext={() => setCurrentStepIndex((index) => index + 1)}
          />
        </StepsCard>
      </PageWrapper>
      <AddMedicationConfirmModal
        open={isConfirmingCancel}
        title={t(emarV2Constants.ADD_MEDICATION_CANCEL_TITLE)}
        body={t(emarV2Constants.ADD_MEDICATION_CANCEL_BODY)}
        cancelLabel={t(emarV2Constants.GO_BACK)}
        onConfirm={leave}
        onCancel={() => setIsConfirmingCancel(false)}
      />
    </>
  )
}

const { spacing } = cssVars

const StepsCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: ${spacing[400]};
  padding: ${spacing[300]};
`

const StepsHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[100]};
`

export default AddMedicationScreen
